import React, { useEffect, useState, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, X, Search, UserPlus, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { usePageHeader } from '../components/Layout';
import { Avatar, SectionCard, Chip, EmptyState } from '../design/primitives';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Teams() {
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, isWorkspaceAdmin } = useWorkspace();
  // Backend require_workspace_admin gates team create/update/delete in workspace context.
  const canManage = activeWorkspaceId
    ? isWorkspaceAdmin
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_manage_teams')
      );

  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const emptyForm = { name: '', description: '', projectIds: [] };
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [memberUserId, setMemberUserId] = useState('');

  useEffect(() => {
    Promise.all([fetchTeams(), fetchUsers(), fetchProjects()]).catch((err) =>
      console.error('Veri yükleme hatası:', err)
    );
  }, []);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`);
      const sorted = [...res.data].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'tr')
      );
      setTeams(sorted);
    } catch {
      toast.error('Takımlar yüklenemedi');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch {
      /* noop */
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch {
      /* noop */
    }
  };

  const userById = (id) => users.find((u) => u.id === id);

  const filteredTeams = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter(
      (t) =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q)
    );
  }, [teams, query]);

  const totalMembers = useMemo(
    () =>
      teams.reduce(
        (sum, t) => sum + ((t.memberIds || []).filter(userById).length || 0),
        0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teams, users]
  );

  const activeProjects = useMemo(() => {
    const ids = new Set();
    teams.forEach((t) => (t.projectIds || []).forEach((id) => ids.add(id)));
    return ids.size;
  }, [teams]);

  // Create
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teams`, createForm);
      toast.success('Takım oluşturuldu');
      setCreateOpen(false);
      setCreateForm(emptyForm);
      fetchTeams();
    } catch (err) {
      toast.error(
        'Takım oluşturulamadı: ' + (err.response?.data?.detail || 'Hata')
      );
    }
  };

  // Edit
  const openEdit = (team) => {
    setSelectedTeam(team);
    setEditForm({
      name: team.name,
      description: team.description || '',
      projectIds: team.projectIds || [],
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/teams/${selectedTeam.id}`, editForm);
      toast.success('Takım güncellendi');
      setEditOpen(false);
      fetchTeams();
    } catch (err) {
      toast.error(
        'Güncellenemedi: ' + (err.response?.data?.detail || 'Hata')
      );
    }
  };

  // Delete
  const openDelete = (team) => {
    setSelectedTeam(team);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/teams/${selectedTeam.id}`);
      toast.success('Takım silindi');
      setDeleteOpen(false);
      fetchTeams();
    } catch (err) {
      toast.error('Silinemedi: ' + (err.response?.data?.detail || 'Hata'));
    }
  };

  // Members
  const openAddMember = (team) => {
    setSelectedTeam(team);
    setMemberUserId('');
    setAddMemberOpen(true);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberUserId) return;
    try {
      await axios.post(`${API}/teams/${selectedTeam.id}/members`, null, {
        params: { user_id: memberUserId },
      });
      toast.success('Üye eklendi');
      setAddMemberOpen(false);
      fetchTeams();
    } catch {
      toast.error('Üye eklenemedi');
    }
  };

  const handleRemoveMember = async (teamId, uid, uname) => {
    if (!window.confirm(`${uname} takımdan çıkarılsın mı?`)) return;
    try {
      await axios.delete(`${API}/teams/${teamId}/members/${uid}`);
      toast.success('Üye çıkarıldı');
      fetchTeams();
    } catch (err) {
      toast.error('Çıkarılamadı: ' + (err.response?.data?.detail || 'Hata'));
    }
  };

  const toggleProjectIn = (list, id) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const breadcrumb = useMemo(() => [{ label: 'Takımlar' }], []);
  const headerActions = useMemo(() => {
    if (!canManage) return null;
    return (
      <button
        className="ox-btn ox-btn-primary"
        onClick={() => {
          setCreateForm(emptyForm);
          setCreateOpen(true);
        }}
      >
        <Plus size={14} /> Yeni Takım
      </button>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);
  usePageHeader(breadcrumb, headerActions, [canManage]);

  return (
    <div className="ox-page space-y-5" data-testid="teams-page">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Toplam Takım" value={teams.length} />
        <StatTile label="Toplam Üye" value={totalMembers} />
        <StatTile label="Aktif Projeler" value={activeProjects} />
        <StatTile
          label="Lidersiz Takım"
          value={
            teams.filter(
              (t) =>
                !(t.leaderIds && t.leaderIds.length) && !t.leaderId
            ).length
          }
        />
      </div>

      {/* Filter bar */}
      <div className="ox-surface p-3 flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]">
            <Search size={14} />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Takım ara..."
            className="ox-input pl-8 w-full"
          />
        </div>
        {query && (
          <button className="ox-btn ox-btn-ghost" onClick={() => setQuery('')}>
            <X size={14} /> Temizle
          </button>
        )}
      </div>

      {/* Team grid */}
      {filteredTeams.length === 0 ? (
        <div className="ox-surface">
          <EmptyState
            icon={<Users size={32} />}
            title={teams.length === 0 ? 'Henüz takım yok' : 'Eşleşen takım bulunamadı'}
            description={teams.length === 0
              ? 'Görevleri ortaklaşa yürütmek için bir takım oluşturun. Üyeler ve projeler takıma eklenebilir.'
              : 'Aramayı değiştirin veya filtreleri temizleyin.'}
            action={canManage && teams.length === 0 && (
              <button className="ox-btn ox-btn-primary" onClick={() => setCreateOpen(true)}>
                <Plus size={14} /> İlk Takımı Oluştur
              </button>
            )}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredTeams.map((team) => {
            const leaderIds =
              team.leaderIds && team.leaderIds.length
                ? team.leaderIds
                : team.leaderId
                ? [team.leaderId]
                : [];
            const activeMembers = (team.memberIds || []).filter(userById);
            const isOpen = expanded[team.id];
            const shown = isOpen ? activeMembers : activeMembers.slice(0, 5);
            const teamProjects = (team.projectIds || [])
              .map((id) => projects.find((p) => p.id === id))
              .filter(Boolean);

            return (
              <SectionCard
                key={team.id}
                pad={false}
                title={
                  <div className="flex items-center gap-2">
                    <span>{team.name}</span>
                    <Chip>{activeMembers.length} üye</Chip>
                  </div>
                }
                subtitle={team.description || '—'}
                right={
                  canManage ? (
                    <div className="flex items-center gap-1">
                      <button
                        className="ox-btn ox-btn-ghost"
                        onClick={() => openAddMember(team)}
                        title="Üye ekle"
                      >
                        <UserPlus size={14} />
                      </button>
                      <button
                        className="ox-btn ox-btn-ghost"
                        onClick={() => openEdit(team)}
                        title="Düzenle"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="ox-btn ox-btn-ghost"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => openDelete(team)}
                        title="Sil"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ) : null
                }
              >
                <div className="p-4 space-y-4">
                  {leaderIds.length > 0 && (
                    <div>
                      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-2">
                        Liderler
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {leaderIds.map((lid) => {
                          const u = userById(lid);
                          if (!u) return null;
                          return (
                            <div
                              key={lid}
                              className="flex items-center gap-2 ox-surface-muted px-2 py-1 rounded-md"
                            >
                              <Avatar u={u} imgSrc={u.picture} size={20} />
                              <span className="text-xs">{u.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {teamProjects.length > 0 && (
                    <div>
                      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-2">
                        Projeler
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {teamProjects.map((p) => (
                          <Chip key={p.id}>{p.name}</Chip>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide mb-2">
                      Üyeler ({activeMembers.length})
                    </div>
                    {activeMembers.length === 0 ? (
                      <div className="text-xs text-[color:var(--text-muted)] italic">
                        Henüz üye yok
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {shown.map((mid) => {
                          const u = userById(mid);
                          return (
                            <div
                              key={mid}
                              className="ox-row-hover group flex items-center justify-between px-2 py-1.5 rounded-md"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Avatar u={u} imgSrc={u.picture} size={22} />
                                <span className="text-sm truncate">
                                  {u.name}
                                </span>
                              </div>
                              {canManage && (
                                <button
                                  onClick={() =>
                                    handleRemoveMember(team.id, mid, u.name)
                                  }
                                  className="opacity-0 group-hover:opacity-100 transition-opacity ox-btn ox-btn-ghost"
                                  style={{ color: 'var(--danger)' }}
                                  title="Çıkar"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                        {activeMembers.length > 5 && (
                          <button
                            className="ox-btn ox-btn-ghost w-full text-xs"
                            onClick={() =>
                              setExpanded((s) => ({
                                ...s,
                                [team.id]: !s[team.id],
                              }))
                            }
                          >
                            {isOpen ? (
                              <>
                                <ChevronUp size={12} /> Daha az
                              </>
                            ) : (
                              <>
                                <ChevronDown size={12} />{' '}
                                {activeMembers.length - 5} üye daha
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {createOpen && (
        <div
          className="ox-modal-backdrop"
          onClick={() => setCreateOpen(false)}
        >
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleCreate}>
              <div className="ox-modal-head">
                <h3>Yeni Takım</h3>
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setCreateOpen(false)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="ox-modal-body space-y-3">
                <div className="ox-field">
                  <label>Takım Adı *</label>
                  <input
                    value={createForm.name}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, name: e.target.value })
                    }
                    className="ox-input"
                    placeholder="Örn: Frontend Ekibi"
                    required
                  />
                </div>
                <div className="ox-field">
                  <label>Açıklama</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        description: e.target.value,
                      })
                    }
                    className="ox-textarea"
                    rows={3}
                    placeholder="Takımın görevi ve sorumlulukları"
                  />
                </div>
                <div className="ox-field">
                  <label>
                    Projeler ({createForm.projectIds.length} seçili)
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {projects.length === 0 && (
                      <span className="text-xs text-[color:var(--text-muted)]">
                        Henüz proje yok
                      </span>
                    )}
                    {projects.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="ox-pick"
                        data-on={createForm.projectIds.includes(p.id)}
                        onClick={() =>
                          setCreateForm({
                            ...createForm,
                            projectIds: toggleProjectIn(
                              createForm.projectIds,
                              p.id
                            ),
                          })
                        }
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ox-modal-foot">
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setCreateOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editOpen && selectedTeam && (
        <div className="ox-modal-backdrop" onClick={() => setEditOpen(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleUpdate}>
              <div className="ox-modal-head">
                <h3>Takımı Düzenle</h3>
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setEditOpen(false)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="ox-modal-body space-y-3">
                <div className="ox-field">
                  <label>Takım Adı *</label>
                  <input
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    className="ox-input"
                    required
                  />
                </div>
                <div className="ox-field">
                  <label>Açıklama</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        description: e.target.value,
                      })
                    }
                    className="ox-textarea"
                    rows={3}
                  />
                </div>
                <div className="ox-field">
                  <label>Projeler ({editForm.projectIds.length} seçili)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {projects.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="ox-pick"
                        data-on={editForm.projectIds.includes(p.id)}
                        onClick={() =>
                          setEditForm({
                            ...editForm,
                            projectIds: toggleProjectIn(
                              editForm.projectIds,
                              p.id
                            ),
                          })
                        }
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ox-modal-foot">
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setEditOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {addMemberOpen && selectedTeam && (
        <div
          className="ox-modal-backdrop"
          onClick={() => setAddMemberOpen(false)}
        >
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleAddMember}>
              <div className="ox-modal-head">
                <h3>Üye Ekle — {selectedTeam.name}</h3>
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setAddMemberOpen(false)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="ox-modal-body">
                <div className="ox-field">
                  <label>Kullanıcı Seç</label>
                  <select
                    value={memberUserId}
                    onChange={(e) => setMemberUserId(e.target.value)}
                    className="ox-select"
                    required
                  >
                    <option value="">— Seçin —</option>
                    {users
                      .filter(
                        (u) => !(selectedTeam.memberIds || []).includes(u.id)
                      )
                      .sort((a, b) =>
                        (a.name || '').localeCompare(b.name || '', 'tr')
                      )
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="ox-modal-foot">
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setAddMemberOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteOpen && selectedTeam && (
        <div
          className="ox-modal-backdrop"
          onClick={() => setDeleteOpen(false)}
        >
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <h3>Takımı Sil</h3>
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <p className="text-sm">
                <strong>{selectedTeam.name}</strong> takımını silmek
                istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOpen(false)}
              >
                İptal
              </button>
              <button className="ox-btn ox-btn-danger" onClick={handleDelete}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="ox-surface p-4">
      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-semibold ox-mono mt-1">{value}</div>
    </div>
  );
}
